import { useEffect, useRef, useState, useCallback } from 'react';

export const REFRESH_EVENT = 'catcare:refresh';

/**
 * Infinite-scroll + auto-refresh for admin list pages.
 *
 * - Calls `load(page, append)` to fetch a page. `append=true` appends results.
 * - Exposes a `sentinelRef` to attach to the bottom element; when it becomes
 *   visible, the next page is loaded automatically.
 * - Listens for the global `catcare:refresh` window event (dispatched when a
 *   new notification arrives or pending counts increase) and refetches page 1.
 * - Exposes a manual `refresh()` to reset to page 1.
 */
export interface UseListRefreshOptions<T> {
  initialItems: T[];
  pageSize: number;
  hasMore: (loadedCount: number, lastPage: number) => boolean;
  load: (page: number, append: boolean) => Promise<{ items: T[]; hasMore: boolean }>;
}

export function useListRefresh<T>({
  initialItems,
  pageSize,
  hasMore,
  load,
}: UseListRefreshOptions<T>) {
  const [items, setItems] = useState<T[]>(initialItems);
  const [page, setPage] = useState(1);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fetchPage = useCallback(
    async (p: number, append: boolean) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      if (p === 1) setRefreshing(true);
      else setLoadingMore(true);
      try {
        const { items: newItems, hasMore: more } = await load(p, append);
        setItems(prev => (append ? [...prev, ...newItems] : newItems));
        setCanLoadMore(more);
        setPage(p);
      } catch (err) {
        console.warn('[useListRefresh] load failed', err);
      } finally {
        loadingRef.current = false;
        setRefreshing(false);
        setLoadingMore(false);
        setInitialLoading(false);
      }
    },
    [load]
  );

  // Initial fetch + refetch when `load` changes (e.g. filters changed).
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  // Infinite scroll via IntersectionObserver.
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!canLoadMore) return;
    const obs = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && canLoadMore && !loadingRef.current) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(sentinelRef.current);
    observerRef.current = obs;
    return () => {
      obs.disconnect();
      observerRef.current = null;
    };
  }, [canLoadMore, page, fetchPage]);

  // Global refresh event: refetch page 1 (replace).
  useEffect(() => {
    const handler = () => {
      fetchPage(1, false);
    };
    window.addEventListener(REFRESH_EVENT, handler as EventListener);
    return () => window.removeEventListener(REFRESH_EVENT, handler as EventListener);
  }, [fetchPage]);

  const refresh = useCallback(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  return {
    items,
    setItems,
    page,
    canLoadMore,
    loadingMore,
    refreshing,
    initialLoading,
    sentinelRef,
    refresh,
  };
}
